<template>
  <div class="tg-overlay">
    <div class="tg-card">
      <section class="content">
        <!-- Заголовок с прогрессом -->
        <div class="header-section">
          <h1 class="title">📦 Создание заказа</h1>
          <p class="subtitle">Заполните все поля для оформления</p>
          <div class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: `${completionPercentage}%` }"></div>
            </div>
            <div class="progress-text">{{ Math.round(completionPercentage) }}% заполнено</div>
          </div>
        </div>

        <!-- Основная форма -->
        <div class="form-container">
          <!-- Товар -->
          <div class="block card-block">
            <div class="block-header">
              <span class="block-icon">📦</span>
              <label>Товар</label>
              <span class="block-required">*</span>
            </div>
            <button
              type="button"
              class="product-picker-trigger"
              @click="openProductPicker"
            >
              <template v-if="selectedProduct">
                <div class="product-picker-thumb">
                  <img v-if="selectedProduct.photo_url" :src="selectedProduct.photo_url" alt="" />
                  <div v-else class="product-picker-thumb-placeholder">📦</div>
                </div>
                <span class="product-picker-name">{{ selectedProduct.name }}</span>
              </template>
              <span v-else class="product-picker-placeholder">Выберите товар</span>
              <div class="select-arrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </button>
          </div>

          <!-- Размер и количество -->
          <div class="row">
            <div class="block card-block">
              <div class="block-header">
                <span class="block-icon">📏</span>
                <label>Размер</label>
                <span class="block-required">*</span>
              </div>
              <div class="select-wrapper">
                <select v-model="order.size" @change="onSizeSelect" :disabled="!order.product_id">
                  <option disabled value="">Выберите размер</option>
                  <option 
                    v-for="s in availableSizes" 
                    :key="s.size_name" 
                    :value="s.size_name"
                    :data-quantity="s.quantity"
                  >
                    {{ s.size_name }}
                    <span v-if="s.quantity > 0" class="size-stock">{{ s.quantity }} шт.</span>
                  </option>
                </select>
                <div class="select-arrow">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="block-footer" v-if="order.size">
                <span class="hint success">✓ В наличии: {{ maxQuantity }} шт.</span>
              </div>
            </div>

            <div class="block card-block">
              <div class="block-header">
                <span class="block-icon">🔢</span>
                <label>Количество</label>
                <span class="block-required">*</span>
              </div>
              <div class="quantity-controls">
                <button 
                  class="quantity-btn" 
                  @click="decrementQuantity"
                  :disabled="!order.size || order.quantity <= 1"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <input
                  type="number"
                  min="1"
                  :max="maxQuantity"
                  v-model.number="order.quantity"
                  @input="validateQuantity"
                  :class="{ 'input-error': quantityError }"
                  :disabled="!order.size"
                />
                <button 
                  class="quantity-btn" 
                  @click="incrementQuantity"
                  :disabled="!order.size || order.quantity >= maxQuantity"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 5v14M5 12h14" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              <!-- Быстрые кнопки количества -->
              <div class="quick-quantity" v-if="maxQuantity > 1">
                <div class="quick-quantity-label">Быстро:</div>
                <div class="quick-quantity-buttons">
                  <button 
                    v-for="qty in quickQuantities"
                    :key="qty"
                    class="quick-qty-btn"
                    :class="{ 'active': order.quantity === qty }"
                    @click="setQuickQuantity(qty)"
                    :disabled="qty > maxQuantity"
                  >
                    {{ qty }}
                  </button>
                </div>
              </div>
              <div class="block-footer">
                <span v-if="quantityError" class="error">
                  ⚠️ Максимум: {{ maxQuantity }} шт.
                </span>
                <span v-else-if="order.size" class="hint">
                  Выбрано: {{ order.quantity }} из {{ maxQuantity }} шт.
                </span>
              </div>
            </div>
          </div>

          <!-- Информация о заказе -->
          <div class="row">
            <div class="block card-block">
              <div class="block-header">
                <span class="block-icon">🏷️</span>
                <label>Номер заказа</label>
                <span class="block-required">*</span>
              </div>
              <div class="input-with-icon">
                <span class="input-prefix">#</span>
                <input 
                  v-model="order.order_number" 
                  inputmode="numeric" 
                  placeholder="123456"
                  @input="formatOrderNumber"
                />
              </div>
            </div>

            <div class="block card-block">
              <div class="block-header">
                <span class="block-icon">💰</span>
                <label>Цена</label>
                <span class="block-required">*</span>
              </div>
              <div class="input-with-icon">
                <span class="input-prefix">₽</span>
                <input 
                  type="number" 
                  v-model.number="order.price" 
                  placeholder="0"
                  min="0"
                  @input="formatPrice"
                />
              </div>
              <div class="price-suggestions" v-if="suggestedPrices.length > 0">
                <div class="suggestions-buttons">
                  <button 
                    v-for="price in suggestedPrices"
                    :key="price"
                    class="price-suggestion"
                    :class="{ 'active': order.price === price }"
                    @click="order.price = price"
                  >
                    {{ formatPrice(price) }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Доставка -->
          <div class="block card-block">
            <div class="block-header">
              <span class="block-icon">🚚</span>
              <label>Служба доставки</label>
              <span class="block-required">*</span>
            </div>
            <div class="delivery-grid">
              <div 
                v-for="company in deliveryCompanies"
                :key="company.id"
                class="delivery-option"
                :class="{ 'selected': order.transport_company === company.name }"
                @click="order.transport_company = company.name"
              >
                <div class="delivery-icon">{{ company.icon }}</div>
                <div class="delivery-name">{{ company.name }}</div>
              </div>
            </div>
          </div>

          <!-- Трек-номер -->
          <div class="block card-block">
            <div class="block-header">
              <span class="block-icon">📮</span>
              <label>Трек-номер</label>
              <span class="block-required">*</span>
              <span class="optional-hint" v-if="!order.transport_company">(выберите службу)</span>
            </div>
            <div class="input-with-suffix">
              <input 
                v-model="order.track_number" 
                :placeholder="trackPlaceholder"
                :disabled="!order.transport_company"
              />
              <button 
                v-if="order.track_number"
                class="clear-btn"
                @click="order.track_number = ''"
                title="Очистить"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            <div class="block-footer" v-if="order.transport_company && trackExample">
              <span class="hint">Пример: {{ trackExample }}</span>
            </div>
          </div>

          <!-- Итоговая информация -->
          <div class="summary-card" v-if="order.price && order.quantity">
            <div class="summary-header">
              <span class="summary-icon">📊</span>
              <h4>Итого</h4>
            </div>
            <div class="summary-content">
              <div class="summary-row">
                <span>Количество:</span>
                <strong>{{ order.quantity }} шт.</strong>
              </div>
              <div class="summary-row">
                <span>Цена за шт:</span>
                <strong>{{ formatPrice(order.price) }} ₽</strong>
              </div>
              <div class="summary-total">
                <span>Сумма заказа:</span>
                <strong class="total-amount">{{ formatPrice(order.price * order.quantity) }} ₽</strong>
              </div>
            </div>
          </div>

          <!-- Валидация формы -->
          <div class="validation-status" v-if="!isFormValid && hasValidationErrors">
            <div class="validation-icon">⚠️</div>
            <div class="validation-text">
              Заполните все обязательные поля
              <div class="validation-hint">Поля отмеченные * обязательны</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Полноэкранный выбор товара -->
      <div class="product-picker-screen" v-if="isProductPickerOpen">
        <div class="product-picker-screen-header">
          <h2>Выберите товар</h2>
        </div>

        <div class="product-picker-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="7"/>
            <path d="M21 21l-4.35-4.35" stroke-linecap="round"/>
          </svg>
          <input
            ref="productSearchInput"
            v-model="productSearchQuery"
            type="text"
            placeholder="Поиск по названию..."
          />
          <button
            v-if="productSearchQuery"
            type="button"
            class="product-picker-search-clear"
            @click="productSearchQuery = ''"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="product-picker-list">
          <div
            v-for="p in filteredProductList"
            :key="p.id"
            class="product-picker-option"
            :class="{ 'selected': order.product_id === p.id }"
            @click="selectProduct(p)"
          >
            <div class="product-picker-thumb">
              <img v-if="p.photo_url" :src="p.photo_url" alt="" />
              <div v-else class="product-picker-thumb-placeholder">📦</div>
            </div>
            <div class="product-picker-info">
              <div class="product-picker-name">{{ p.name }}</div>
              <div class="product-picker-stock">
                <span class="stock-dot" :class="getStockClass(p)"></span>
                {{ p.total_stock }} шт.
              </div>
            </div>
          </div>
          <div v-if="filteredProductList.length === 0" class="product-picker-empty">
            {{ sortedProductList.length === 0 ? 'Нет товаров в наличии' : 'Ничего не найдено' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/api';

export default {
  data() {
    return {
      productList: [],
      isProductPickerOpen: false,
      productSearchQuery: '',
      quantityError: false,
      isSubmitting: false,
      window: window,
      quickQuantities: [1, 3, 5, 10],
      deliveryCompanies: [
        { id: 1, name: 'СДЭК', icon: '🚚' },
        { id: 2, name: 'Почта России', icon: '📮' },
        { id: 3, name: 'Boxberry', icon: '📦' },
        { id: 4, name: 'Avito', icon: '🛒' },
        { id: 5, name: 'Яндекс Доставка', icon: '🚕' },
        { id: 6, name: '5Post', icon: '📬' },
      ],
      order: {
        product_id: '',
        size: '',
        quantity: 1,
        order_number: '',
        price: null,
        transport_company: '',
        track_number: ''
      }
    }
  },

  computed: {
    availableSizes() {
      return this.productList.find(p => p.id === this.order.product_id)?.presence || [];
    },
    maxQuantity() {
      return this.availableSizes.find(s => s.size_name === this.order.size)?.quantity || 0;
    },
    isFormValid() {
      const o = this.order;
      return (
        o.product_id &&
        o.size &&
        o.quantity > 0 &&
        o.quantity <= this.maxQuantity &&
        o.order_number &&
        o.price > 0 &&
        o.transport_company &&
        o.track_number &&
        !this.quantityError
      );
    },
    hasValidationErrors() {
      const o = this.order;
      return (
        !o.product_id ||
        !o.size ||
        !o.quantity ||
        !o.order_number ||
        !o.price ||
        !o.transport_company ||
        !o.track_number
      );
    },
    completionPercentage() {
      let filledFields = 0;
      const totalFields = 7;
      
      if (this.order.product_id) filledFields++;
      if (this.order.size) filledFields++;
      if (this.order.quantity > 0 && this.order.quantity <= this.maxQuantity) filledFields++;
      if (this.order.order_number) filledFields++;
      if (this.order.price > 0) filledFields++;
      if (this.order.transport_company) filledFields++;
      if (this.order.track_number) filledFields++;
      
      return (filledFields / totalFields) * 100;
    },
    sortedProductList() {
      return [...this.productList]
        .filter(p => p.total_stock > 0)
        .sort((a, b) => b.total_stock - a.total_stock);
    },
    filteredProductList() {
      const query = this.productSearchQuery.trim().toLowerCase();
      if (!query) return this.sortedProductList;
      return this.sortedProductList.filter(p => p.name.toLowerCase().includes(query));
    },
    selectedProduct() {
      return this.productList.find(p => p.id === this.order.product_id) || null;
    },
    suggestedPrices() {
      if (!this.order.product_id || !this.order.price) return [];
      const basePrice = this.order.price || 1000;
      return [
        Math.round(basePrice / 100) * 100,
        Math.round(basePrice / 500) * 500,
        Math.round(basePrice / 1000) * 1000
      ].filter((v, i, a) => a.indexOf(v) === i).slice(0, 3);
    },
    trackPlaceholder() {
      if (!this.order.transport_company) return 'Выберите службу доставки';
      return 'Введите трек-номер';
    },
    trackExample() {
      switch(this.order.transport_company) {
        case 'СДЭК': return 'СМ000123456РУ';
        case 'Почта России': return 'RR123456789RU';
        case 'Boxberry': return 'BX12345678';
        case 'Avito': return 'AV12345678';
        default: return '1234567890';
      }
    },
    submitButtonText() {
      if (this.isFormValid) {
        const total = this.order.price * this.order.quantity;
        return `Добавить заказ • ${this.formatPrice(total)} ₽`;
      }
      return 'Добавить заказ';
    }
  },

  watch: {
    'order.product_id'() {
      this.order.size = '';
      this.order.quantity = 1;
      this.quantityError = false;
    },
    'order.size'() {
      this.order.quantity = 1;
      this.quantityError = false;
    },
    maxQuantity() {
      this.validateQuantity();
    },
    isFormValid() {
      this.syncMainButton();
    },
    isSubmitting() {
      this.syncMainButton();
    },
    isProductPickerOpen() {
      this.syncMainButton();
    },
    submitButtonText() {
      this.syncMainButton();
    }
  },

  async mounted() {
    document.body.style.overflow = 'hidden';

    // Telegram Back Button
    if (window.Telegram?.WebApp?.BackButton) {
      window.Telegram.WebApp.BackButton.show();
      window.Telegram.WebApp.BackButton.onClick(() => {
        if (this.isProductPickerOpen) {
          this.closeProductPicker();
        } else {
          this.$emit('close');
        }
      });
    }

    // Настройка Telegram Web App
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.expand();
      window.Telegram.WebApp.setHeaderColor('#1a1a1a');
      window.Telegram.WebApp.setBackgroundColor('#0f0f0f');
    }

    // Кнопка "Добавить заказ" из Telegram API
    if (window.Telegram?.WebApp?.MainButton) {
      window.Telegram.WebApp.MainButton.setParams({
        color: '#2AABEE',
        text_color: '#FFFFFF'
      });
      window.Telegram.WebApp.MainButton.onClick(this.submit);
      this.syncMainButton();
    }

    try {
      const { data } = await api.get('/manager/products/presence');
      this.productList = data.map(p => ({
        ...p,
        total_stock: p.presence?.reduce((sum, s) => sum + s.quantity, 0) || 0
      }));
    } catch (error) {
      console.error('Ошибка при загрузке товаров:', error);
    }
  },

  beforeUnmount() {
    document.body.style.overflow = '';

    if (window.Telegram?.WebApp?.BackButton) {
      window.Telegram.WebApp.BackButton.hide();
      window.Telegram.WebApp.BackButton.offClick();
    }

    if (window.Telegram?.WebApp?.MainButton) {
      window.Telegram.WebApp.MainButton.offClick(this.submit);
      window.Telegram.WebApp.MainButton.hide();
    }
  },

  methods: {
    validateQuantity() {
      this.quantityError = this.order.quantity > this.maxQuantity;
      if (this.quantityError) {
        this.order.quantity = this.maxQuantity;
      }
    },

    decrementQuantity() {
      if (this.order.quantity > 1) {
        this.order.quantity--;
      }
    },

    incrementQuantity() {
      if (this.order.quantity < this.maxQuantity) {
        this.order.quantity++;
      }
    },

    setQuickQuantity(qty) {
      if (qty <= this.maxQuantity) {
        this.order.quantity = qty;
      }
    },

    openProductPicker() {
      this.isProductPickerOpen = true;
      this.productSearchQuery = '';
      this.$nextTick(() => this.$refs.productSearchInput?.focus());
    },

    selectProduct(product) {
      this.order.product_id = product.id;
      this.closeProductPicker();
      this.onProductSelect();
    },

    closeProductPicker() {
      this.isProductPickerOpen = false;
      this.productSearchQuery = '';
    },

    getStockClass(product) {
      const stock = product.total_stock;
      if (stock > 10) return 'stock-high';
      if (stock > 0 && stock <= 10) return 'stock-medium';
      return 'stock-low';
    },

    formatPrice(price) {
      if (!price) return '0';
      return new Intl.NumberFormat('ru-RU').format(price);
    },

    formatOrderNumber() {
      // Убираем все нецифровые символы
      this.order.order_number = this.order.order_number.replace(/\D/g, '');
    },

    onProductSelect() {
      // Автофокус на выбор размера
      setTimeout(() => {
        const sizeSelect = this.$el.querySelector('select[v-model="order.size"]');
        if (sizeSelect) sizeSelect.focus();
      }, 100);
    },

    onSizeSelect() {
      // Автофокус на количество
      setTimeout(() => {
        const quantityInput = this.$el.querySelector('input[type="number"][v-model.number="order.quantity"]');
        if (quantityInput) quantityInput.focus();
      }, 100);
    },

    handleBack() {
      this.$emit('close');
    },

    async submit() {
      if (!this.isFormValid) return;
      
      this.isSubmitting = true;
      
      try {
        const orderData = {
          ...this.order,
          quantity: +this.order.quantity,
          price: +this.order.price,
          product_id: +this.order.product_id
        };
        
        await api.post('/manager/orders', orderData);
        
        // Уведомление в Telegram
        if (window.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert('✅ Заказ успешно создан!');
        }
        
        this.$emit('close');
        this.$emit('order-created');
        
      } catch (error) {
        console.error('Ошибка при создании заказа:', error);
        
        // Показываем ошибку в Telegram
        if (window.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert(`❌ Ошибка: ${error.response?.data?.detail || error.message}`);
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    syncMainButton() {
      const mainButton = window.Telegram?.WebApp?.MainButton;
      if (!mainButton) return;

      mainButton.setText(this.submitButtonText);

      if (this.isSubmitting) {
        mainButton.showProgress(false);
        mainButton.disable();
        return;
      }

      mainButton.hideProgress();

      if (this.isFormValid && !this.isProductPickerOpen) {
        mainButton.enable();
        mainButton.show();
      } else {
        mainButton.hide();
      }
    }
  }
}
</script>

<style scoped>
.tg-overlay {
  position: fixed;
  inset: 0;
  background: #0f0f0f;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 1010;
  overflow-y: auto;
}

.tg-card {
  margin-top: 40vw;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40vw);
  display: flex;
  flex-direction: column;
  border-radius: 24px 24px 0 0;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(24px);
  box-shadow: 
    0 -10px 40px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.tg-back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tg-back-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  transform: translateX(-2px);
}

.tg-header-title {
  flex: 1;
}

.tg-badge {
  background: linear-gradient(135deg, #2AABEE, #007AFF);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.content {
  padding: 24px 20px;
  overflow-y: auto;
  flex: 1;
}

/* Заголовок */
.header-section {
  padding-top: 12vw;
  margin-bottom: 28px;
}

.title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #2AABEE, #007AFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
}

.subtitle {
  margin: 8px 0 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

/* Прогресс бар */
.progress-container {
  margin-top: 20px;
}

.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #2AABEE, #007AFF);
  border-radius: 3px;
  transition: width 0.6s ease;
  box-shadow: 0 0 10px rgba(42, 171, 238, 0.3);
}

.progress-text {
  text-align: right;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.stock-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.stock-high {
  background: #06D6A0;
  box-shadow: 0 0 6px rgba(6, 214, 160, 0.4);
}

.stock-medium {
  background: #FFD166;
  box-shadow: 0 0 6px rgba(255, 209, 102, 0.4);
}

.stock-low {
  background: #EF476F;
  box-shadow: 0 0 6px rgba(239, 71, 111, 0.4);
}

/* Блоки формы */
.form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card-block {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px;
  transition: all 0.2s ease;
}

.card-block:focus-within {
  border-color: #2AABEE;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 20px rgba(42, 171, 238, 0.1);
}

.block-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.block-icon {
  font-size: 18px;
  opacity: 0.8;
}

.block-header label {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  flex: 1;
}

.block-required {
  color: #EF476F;
  font-size: 16px;
  font-weight: bold;
}

.optional-hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: normal;
}

/* Селекты */
.select-wrapper {
  position: relative;
}

.select-wrapper select {
  width: 100%;
  padding: 16px 20px;
  padding-right: 48px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 15px;
  font-weight: 500;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select-wrapper select:focus {
  outline: none;
  border-color: #2AABEE;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(42, 171, 238, 0.1);
}

.select-wrapper select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.select-arrow {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  pointer-events: none;
}

.size-stock {
  float: right;
  color: #FFD166;
  font-size: 12px;
  font-weight: 500;
  background: rgba(255, 209, 102, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
}

/* Кастомный выбор товара */
.product-picker-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 48px 10px 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  position: relative;
}

.product-picker-trigger:focus {
  outline: none;
  border-color: #2AABEE;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(42, 171, 238, 0.1);
}

.product-picker-placeholder {
  color: rgba(255, 255, 255, 0.4);
  flex: 1;
}

.product-picker-thumb {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-picker-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-picker-thumb-placeholder {
  font-size: 18px;
  opacity: 0.5;
}

.product-picker-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
  font-weight: 600;
}

/* Полноэкранный выбор товара */
.product-picker-screen {
  position: fixed;
  inset: 0;
  z-index: 1020;
  background: #0f0f0f;
  display: flex;
  flex-direction: column;
}

.product-picker-screen-header {
  display: flex;
  align-items: center;
  padding: calc(12vw + env(safe-area-inset-top, 0px)) 20px 12px;
  flex-shrink: 0;
}

.product-picker-screen-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: white;
}

.product-picker-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin: 0 20px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
}

.product-picker-search input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: white;
  font-size: 14px;
  font-family: inherit;
  padding: 0;
}

.product-picker-search input::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.product-picker-search-clear {
  background: transparent;
  border: none;
  padding: 2px;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.product-picker-search-clear:hover {
  color: white;
}

.product-picker-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px calc(20px + env(safe-area-inset-bottom, 0px));
}

.product-picker-option {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  cursor: pointer;
  transition: all 0.15s ease;
}

.product-picker-option:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.12);
}

.product-picker-option.selected {
  background: rgba(42, 171, 238, 0.15);
  border-color: rgba(42, 171, 238, 0.4);
  box-shadow: 0 4px 12px rgba(42, 171, 238, 0.15);
}

.product-picker-option .product-picker-thumb {
  width: 56px;
  height: 56px;
}

.product-picker-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-picker-info .product-picker-name {
  font-size: 15px;
  white-space: normal;
}

.product-picker-stock {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.product-picker-empty {
  padding: 40px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

/* Контролы количества */
.quantity-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.quantity-btn {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.quantity-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: #2AABEE;
  color: white;
  transform: scale(1.05);
}

.quantity-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.quantity-controls input {
  flex: 1;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  transition: all 0.2s ease;
}

.quantity-controls input:focus {
  outline: none;
  border-color: #2AABEE;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(42, 171, 238, 0.1);
}

.quantity-controls input.input-error {
  border-color: #EF476F;
  background: rgba(239, 71, 111, 0.05);
}

/* Быстрые кнопки количества */
.quick-quantity {
  margin-top: 16px;
}

.quick-quantity-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  margin-bottom: 8px;
  font-weight: 500;
}

.quick-quantity-buttons {
  display: flex;
  gap: 10px;
}

.quick-qty-btn {
  flex: 1;
  padding: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-qty-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  color: white;
}

.quick-qty-btn.active {
  background: linear-gradient(135deg, #2AABEE, #007AFF);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(42, 171, 238, 0.2);
}

.quick-qty-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Инпуты с иконками */
.input-with-icon,
.input-with-suffix {
  position: relative;
}

.input-prefix {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  font-size: 16px;
}

.input-with-icon input {
  padding-left: 40px;
}

.input-with-suffix input {
  padding-right: 48px;
}

.clear-btn {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: rgba(239, 71, 111, 0.1);
  color: #EF476F;
}

.card-block input {
  width: 100%;
  padding: 16px 20px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 15px;
  transition: all 0.2s ease;
}

.card-block input:focus {
  outline: none;
  border-color: #2AABEE;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(42, 171, 238, 0.1);
}

.card-block input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Сетка доставки */
.delivery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.delivery-option {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 16px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.delivery-option:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(42, 171, 238, 0.3);
  transform: translateY(-2px);
}

.delivery-option.selected {
  background: linear-gradient(135deg, rgba(42, 171, 238, 0.15), rgba(0, 122, 255, 0.1));
  border-color: rgba(42, 171, 238, 0.4);
  box-shadow: 0 4px 12px rgba(42, 171, 238, 0.2);
}

.delivery-icon {
  font-size: 20px;
}

.delivery-name {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

/* Предложения по цене */
.price-suggestions {
  margin-top: 12px;
}

.suggestions-buttons {
  display: flex;
  gap: 10px;
}

.price-suggestion {
  flex: 1;
  padding: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.price-suggestion:hover {
  background: rgba(255, 255, 255, 0.08);
  color: white;
}

.price-suggestion.active {
  background: linear-gradient(135deg, #FFD166, #EF9B5A);
  color: white;
  border-color: transparent;
  font-weight: 600;
}

/* Итоговая карточка */
.summary-card {
  background: linear-gradient(135deg, rgba(42, 171, 238, 0.1), rgba(0, 122, 255, 0.05));
  border: 1px solid rgba(42, 171, 238, 0.2);
  border-radius: 16px;
  padding: 20px;
  margin-top: 10px;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.summary-icon {
  font-size: 20px;
}

.summary-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row span {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.summary-row strong {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 600;
}

.summary-total {
  padding-top: 16px;
  margin-top: 8px;
  border-top: 2px solid rgba(42, 171, 238, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.total-amount {
  color: #2AABEE;
  font-size: 20px;
  font-weight: 700;
}

/* Валидация */
.validation-status {
  background: rgba(239, 71, 111, 0.1);
  border: 1px solid rgba(239, 71, 111, 0.2);
  border-radius: 14px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 10px;
}

.validation-icon {
  font-size: 20px;
  color: #EF476F;
  flex-shrink: 0;
}

.validation-text {
  flex: 1;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
}

.validation-hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
}

/* Подвал и подсказки */
.block-footer {
  margin-top: 12px;
}

.hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  display: block;
}

.hint.success {
  color: #06D6A0;
}

.error {
  font-size: 12px;
  color: #EF476F;
  font-weight: 500;
}

.row {
  display: flex;
  gap: 16px;
}

.row > .block {
  flex: 1;
  margin-bottom: 0;
}

/* Адаптивность */
@media (max-width: 768px) {
  .tg-card {
    margin-top: 20vw;
    max-height: calc(100vh - 20vw);
    border-radius: 20px 20px 0 0;
  }
  
  .content {
    padding: 20px 16px;
  }
  
  .title {
    font-size: 24px;
  }
  
  .row {
    flex-direction: column;
    gap: 16px;
  }
  
  .delivery-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .tg-header {
    padding: 14px 16px;
  }
}

@media (max-width: 480px) {
  .tg-card {
    margin-top: 10vw;
    max-height: calc(100vh - 10vw);
    border-radius: 16px 16px 0 0;
  }
  
  .delivery-grid {
    grid-template-columns: 1fr;
  }
  
  .quick-quantity-buttons {
    flex-wrap: wrap;
  }
  
  .quick-qty-btn {
    flex: none;
    width: calc(25% - 8px);
  }
}

/* Кастомный скролл */
.content::-webkit-scrollbar {
  width: 6px;
}

.content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 3px;
}

.content::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #2AABEE, #007AFF);
  border-radius: 3px;
}

.content::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #007AFF, #2AABEE);
}
</style>